import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate, useLocation } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import closeIcon from '../../assets/images/close.png';
import { userAPI, codeAPI } from '../../actions/imsServer';
import CustomSelect from '../select';

// Password validation util
function isValidPassword(pw) {
  const passwordRegex = /^(?!^\d+$)(?!^[a-zA-Z]+$)(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$)[a-zA-Z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{6,12}$/;
  return passwordRegex.test(pw);
}

export const ResetPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { type, email: locationEmail, loginType: locationLoginType } = location.state || {};
    const [email, setEmail] = useState(locationEmail || '');
    const [code, setCode] = useState('');
    const [step, setStep] = useState(0);
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [hasSentCode, setHasSentCode] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    
    // 국가코드 상태 추가
    const [countryCode, setCountryCode] = useState('81');
    
    // loginType 상태 추가 (기본값은 'email')
    const [loginType, setLoginType] = useState(locationLoginType || 'email');
    
    // Check if user is grade 80 or above
    const meInfo = JSON.parse(localStorage.getItem('meInfo'));

    // 전화번호에 국가코드 추가하는 함수
    const addCountryCode = (phoneNumber) => {
        // 이미 +로 시작하는 경우 그대로 반환
        if (phoneNumber.startsWith('+')) {
            return phoneNumber;
        }
        
        // 선택된 국가코드 사용
        const selectedCountryCode = countryCode;
        
        // 전화번호 맨 앞의 0 제거
        const cleanPhoneNumber = phoneNumber.replace(/^0+/, '');
        return `+${selectedCountryCode}${cleanPhoneNumber}`;
    };    

    React.useEffect(() => {
        // Set initial step for high grade users
        if (meInfo?.grade >= 80) {                        
            setStep(1);            
        }
    }, [meInfo]);

    React.useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [countdown]);

    const handleClearEmail = () => setEmail('');
    const handleSendCode = async () => {
        // 버튼을 누르자마자 비활성화
        setCountdown(60);
        setHasSentCode(true);
        
        try {
            // type과 loginType에 따라 다른 유효성 검사 적용
            let isValidEmail, isValidPhone;
            
            if (type === 'findPassword' && loginType === 'email') {
                // findPassword + email인 경우: 이메일만 검사
                isValidEmail = /\S+@\S+\.\S+/.test(email);
                if (!email || !isValidEmail) {
                    setEmailError(true);
                    setEmailErrorMessage(t('auth_invalid_email'));
                    // 유효성 검사 실패 시 버튼 상태 복원
                    setCountdown(0);
                    setHasSentCode(false);
                    return;
                }
                isValidPhone = false; // 전화번호는 검사하지 않음
            } else {
                // 그 외의 경우: 이메일 또는 전화번호 검사
                isValidEmail = /\S+@\S+\.\S+/.test(email);
                isValidPhone = /^[0-9]+$/.test(email);
                
                if (!email || (!isValidEmail && !isValidPhone)) {
                    setEmailError(true);
                    setEmailErrorMessage(t('auth_invalid_id'));
                    // 유효성 검사 실패 시 버튼 상태 복원
                    setCountdown(0);
                    setHasSentCode(false);
                    return;
                }
            }
            
            let loginIdForCheck;
            let typeForCode;
            
            if (isValidPhone) {
                // 전화번호인 경우: 앞의 0 제거, 국가코드 앞에 추가
                const phoneWithoutLeadingZero = email.replace(/^0+/, ''); // 앞의 0 제거
                loginIdForCheck = countryCode + phoneWithoutLeadingZero; // 국가코드 앞에 추가
                typeForCode = 1; // 전화번호 타입
            } else {
                // 이메일인 경우
                loginIdForCheck = email;
                typeForCode = 0; // 이메일 타입
            }
            
            const checkResponse = await userAPI.checkUserExists({ loginId: loginIdForCheck });
            if (checkResponse.data.exists === false) {
                setSnackbar({
                    open: true,
                    message: t('auth_id_not_exists'),
                    severity: 'error'
                });
                // 사용자 존재하지 않음 시 버튼 상태 복원
                setCountdown(0);
                setHasSentCode(false);
                return;
            }
            await codeAPI.codeGenerate({ target: loginIdForCheck, type: typeForCode });
            setSnackbar({
                open: true,
                message: `${t('reset_password_code_sent')} ${email}`,
                severity: 'success'
            });
            // 성공 시에는 이미 설정된 상태 유지
        } catch (error) {
            console.error('인증번호 발송 실패:', error);
            
            // 에러 발생 시 버튼 상태 복원
            setCountdown(0);
            setHasSentCode(false);
            
            // 에러 메시지가 'code_limit_exceeded_per_day'인 경우 특정 메시지 표시
            if (error?.message === 'code_limit_exceeded_per_day') {
                setSnackbar({
                    open: true,
                    message: t('auth_code_limit_exceeded_per_day'),
                    severity: 'error'
                });
            }else{
                setSnackbar({
                    open: true,
                    message: t('app_error'),
                    severity: 'error'
                });
            }
        }
    };


    const handleNext = async () => {
        setLoading(true);
        try {
            let targetForConfirm;
            let typeForConfirm;
            
            // 전화번호인지 확인 (숫자만으로 구성)
            const isValidPhone = /^[0-9]+$/.test(email);
            
            if (isValidPhone) {
                // 전화번호인 경우: 앞의 0 제거, 국가코드 앞에 추가
                const phoneWithoutLeadingZero = email.replace(/^0+/, ''); // 앞의 0 제거
                targetForConfirm = countryCode + phoneWithoutLeadingZero; // 국가코드 앞에 추가
                typeForConfirm = 1; // 전화번호 타입
            } else {
                // 이메일인 경우
                targetForConfirm = email;
                typeForConfirm = 0; // 이메일 타입
            }
            
            const response = await codeAPI.codeConfirm({ target: targetForConfirm, type: typeForConfirm, key: code });
            if (response.result === 'ok') {
                if (type === 'findPassword') {
                    let loginIdForFindPassword;
                    
                    if (isValidPhone) {
                        // 전화번호인 경우: 앞의 0 제거, 국가코드 앞에 추가
                        const phoneWithoutLeadingZero = email.replace(/^0+/, ''); // 앞의 0 제거
                        loginIdForFindPassword = countryCode + phoneWithoutLeadingZero; // 국가코드 앞에 추가
                    } else {
                        // 이메일인 경우
                        loginIdForFindPassword = email;
                    }
                    
                    const findPasswordResponse = await userAPI.findPassword({ loginId: loginIdForFindPassword });
                    setCurrentPassword(findPasswordResponse.data.loginPwd);
                }
                setStep(1);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: `${t('reset_password_code_invalid')}`,
                severity: 'error'
              }); 
        } finally {
            setLoading(false);
        }
    };

    const handleClearCurrentPassword = () => setCurrentPassword('');
    const handleClearPassword = () => setPassword('');
    const handleClearPasswordConfirm = () => setPasswordConfirm('');
    const handleChangePassword = async () => {
        setLoading(true);
        try {            
            let loginIdForChange;
            
            // 사용할 loginId 결정 (email이 있으면 email, 없으면 meInfo.loginId)
            const targetLoginId = email || meInfo.loginId;
            
            // 전화번호인지 확인 (숫자만으로 구성)
            const isValidPhone = /^[0-9]+$/.test(targetLoginId);
            
            if (isValidPhone) {
                // 전화번호인 경우: 앞의 0 제거, 국가코드 앞에 추가
                const phoneWithoutLeadingZero = targetLoginId.replace(/^0+/, ''); // 앞의 0 제거
                loginIdForChange = countryCode + phoneWithoutLeadingZero; // 국가코드 앞에 추가
            } else {
                // 이메일인 경우
                loginIdForChange = targetLoginId;
            }
            
            await userAPI.changePassword({        
                loginId: loginIdForChange,         
                currentPassword,
                newPassword: password
            });            
            // type이 'findPassword'이고 loginType이 'phone'인 경우 전화번호 로그인 화면으로 이동
            if (type === 'findPassword' && loginType === 'phone') {
              navigate('/auth/signin', { 
                state: { 
                  loginType: 'phone',
                  fromPasswordReset: true 
                } 
              });
            } else {
              navigate(-1);
            }            
        } catch (err) {
            alert(t('reset_password_error') + ': ' + (err?.message));
        } finally {
            setLoading(false);
        }
    };

    const handleEmailChange = (event) => {
        let value = event.target.value;
        setEmail(value);
        
        // type과 loginType에 따라 다른 유효성 검사 적용
        if (type === 'findPassword' && loginType === 'email') {
            // findPassword + email인 경우: 이메일만 검사
            const isValidEmail = /\S+@\S+\.\S+/.test(value);
            if (!value || !isValidEmail) {
                setEmailError(true);
                setEmailErrorMessage(t('auth_invalid_email'));
            } else {
                setEmailError(false);
                setEmailErrorMessage('');
            }
        } else if (loginType === 'phone') {            
            const isValidPhone = /^[0-9]+$/.test(value);
            if (!value || !isValidPhone) {
                setEmailError(true);
                setEmailErrorMessage(t('auth_invalid_phone'));
            } else {
                setEmailError(false);
                setEmailErrorMessage('');
            }
        } else {
            // 그 외의 경우: 이메일 또는 전화번호 검사
            const isValidEmail = /\S+@\S+\.\S+/.test(value);
            const isValidPhone = /^[0-9]+$/.test(value);
            
            if (!value || (!isValidEmail && !isValidPhone)) {
                setEmailError(true);
                setEmailErrorMessage(t('auth_invalid_id'));
            } else {
                setEmailError(false);
                setEmailErrorMessage('');
            }
        }
    };

    const handleClearCode = () => setCode('');
    const handleCodeChange = (event) => {
        const value = event.target.value;
        setCode(value);        
    };
      
    return (
      <Box sx={{      
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        padding: { xs: '64px 20px 32px 20px', sm: '64px 0 0 0' },
        flexDirection: 'column',
        alignItems: 'center',
        gap: '50px',
        flex: '1 0 0', 
        alignSelf: 'stretch',
        backgroundColor: type === 'findPassword' ? "#fff" : "transparent",
        position: type === 'findPassword' ? 'absolute' : 'relative',
        top: type === 'findPassword' ? 0 : 'auto',
        left: type === 'findPassword' ? 0 : 'auto',
        right: type === 'findPassword' ? 0 : 'auto',
        bottom: type === 'findPassword' ? 0 : 'auto'
        }}
      >
        {/* type이 'findPassword'일 때만 헤더 표시 */}
        {type === 'findPassword' && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            height: '54px', 
            px: 2, 
            borderBottom: '1px solid rgba(0,0,0,0.10)', 
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,           
          }}>
            <IconButton onClick={() => {
              // type이 'findPassword'이고 loginType이 'phone'인 경우 전화번호 로그인 화면으로 이동
              if (type === 'findPassword' && loginType === 'phone') {
                navigate('/auth/signin', { 
                  state: { 
                    loginType: 'phone',
                    fromPasswordReset: true 
                  } 
                });
              } else {
                navigate(-1);
              }
            }} sx={{ mr: 1 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '15px', color: '#211F20',  }}>
              {t('reset_password_title')}
            </Typography>
          </Box>
        )}
        
        <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose= {() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
              // AOS 웹뷰에서 상단에 안전하게 표시되도록 여백 조정
              mt: { xs: '60px', sm: '20px' }
            }}
        >
            <Alert 
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                sx={{ 
                    width: '100%', 
                    borderRadius: '8px',
                    // AOS 웹뷰에서 더 잘 보이도록 스타일 강화
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                }}
            >
                {snackbar.message}
            </Alert>
        </Snackbar>
        
        {step === 0 ? (
        <>
        <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '40px',
              alignSelf: 'stretch',
              width: { xs: '100%', sm: '100%' },
              maxWidth: { xs: '100%', sm: '400px' },
              margin: { xs: 0, sm: '0 auto' },              
          }}
        >
          <Typography
              sx={{
                  color: 'rgba(0, 0, 0, 0.60)',
                  textAlign: 'center',
                    fontSize: { xs: '15px', sm: '18px' },
                  fontWeight: 500,
                  lineHeight: 'normal',
              }}
          >
              {type === 'findPassword' && loginType === 'email' 
                      ? t('reset_password_verify_email') 
                      : type === 'findPassword' && loginType === 'phone' 
                      ? t('reset_password_verify_phone')
                      : t('reset_password_verify_email_phone')
                  }
          </Typography>
          
          {/* type이 'changePassword'이거나 loginType이 'phone'인 경우에만 국가/지역 선택 표시 */}
          {(type !== 'findPassword' || loginType !== 'email') && (
              <Box
                  sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '10px',
                      alignSelf: 'stretch',
                  }}
              >
                  <Typography
                      sx={{
                          color: 'rgba(0, 0, 0, 0.60)',
                          textAlign: 'center',
                                    fontSize: { xs: '15px', sm: '16px' },
                          fontWeight: 500,
                          lineHeight: 'normal',
                      }}
                  >
                      {t('country_region')}
                  </Typography>
                  <CustomSelect
                      name="country"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      options={[
                          { value: '81', label: t('japan_code') },
                          { value: '886', label: t('taiwan_code') },
                          { value: '62', label: t('indonesia_code') },
                          { value: '82', label: t('korea_code') }
                      ]}
                      sx={{
                          width: { xs: '100%', sm: '400px' },
                          height: '38px',
                          padding: '0px 20px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          alignSelf: 'center',
                          '& .MuiSelect-select': {
                              padding: '0',
                              textAlign: 'left'
                          }
                      }}
                      required
                  />
              </Box>
          )}
          
          <Box
              sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch',
              }}
          >
              <Typography
                  sx={{
                      color: 'rgba(0, 0, 0, 0.60)',
                      textAlign: 'center',
                            fontSize: { xs: '15px', sm: '16px' },
                      fontWeight: 500,
                      lineHeight: 'normal',
                  }}
              >
                  {/* type과 loginType에 따라 다른 라벨 표시 */}
                  {type === 'findPassword' && loginType === 'email' 
                      ? t('user_email') 
                      : type === 'findPassword' && loginType === 'phone' 
                      ? t('user_phone')
                      : t('reset_password_email_phone')
                  }
              </Typography>
              <FormControl sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch'
              }}>
                  <TextField
                      fullWidth
                      placeholder={
                          type === 'findPassword' && loginType === 'email' 
                              ? t('form_email_placeholder') 
                              : type === 'findPassword' && loginType === 'phone' 
                              ? t('form_phone_placeholder') 
                              : t('reset_password_email_phone_placeholder')
                      }
                      value={email}
                      onChange={handleEmailChange}
                      variant="outlined"
                      error={emailError}
                      helperText={emailErrorMessage}
                      sx={{
                          '& .MuiOutlinedInput-root': {
                              height: '38px',
                              borderRadius: '16px',
                              border: '1px solid rgba(0, 0, 0, 0.10)',
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              '& fieldset': {
                                  border: 'none'
                              },
                              '&:hover fieldset': {
                                  border: 'none'
                              },
                              '&.Mui-focused fieldset': {
                                  border: 'none'
                              }
                          },
                          '& .MuiInputBase-input': {
                              fontSize: { xs: '15px', sm: '16px' },
                              padding: '0 16px',
                              display: 'flex',
                              alignItems: 'center'
                          },
                          '& .MuiFormHelperText-root': {
                              margin: '4px 0 0 0',
                              fontSize: '12px',
                          }
                      }}
                      InputProps={{
                          endAdornment: email && (
                              <InputAdornment position="end">
                                  <IconButton onClick={handleClearEmail} edge="end" size="small">
                                      <Box
                                          component="img"
                                          src={closeIcon}
                                          sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                      />
                                  </IconButton>
                              </InputAdornment>
                          ),
                      }}
                  />
              </FormControl>
          </Box>
          <Box
              sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: '20px', sm: '20px' },
                  alignSelf: 'stretch',
              }}
          >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch',
                }}
            >
              <Typography
                  sx={{
                      color: 'rgba(0, 0, 0, 0.60)',
                      textAlign: 'center',
                            fontSize: { xs: '15px', sm: '16px' },
                      fontWeight: 500,
                      lineHeight: 'normal',
                  }}
              >
                  {t('reset_password_verification_code')}
              </Typography>
              <Box
                  sx={{
                      display: 'flex',
                      height: { xs: '38px', sm: '44px' },
                      alignItems: 'flex-start',
                      gap: '10px',
                      alignSelf: 'stretch',
                  }}
              >
                <Box
                    sx={{
                        display: 'flex',
                        height: { xs: '38px', sm: '44px' },
                        padding: '0px 16px',
                        alignItems: 'center',
                        gap: '10px',
                        flex: '1 0 0',
                        borderRadius: '16px',
                        border: '1px solid rgba(0, 0, 0, 0.10)',
                        background: 'rgba(0, 0, 0, 0.03)',
                    }}
                >
                  <TextField
                      fullWidth
                      placeholder={t('reset_password_code_placeholder')}
                      value={code}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(0, 0, 0, 0.30)',
                        textAlign: 'center',
                                fontSize: { xs: '15px', sm: '16px' },
                        fontWeight: 500,
                        lineHeight: 'normal',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: { xs: '15px', sm: '16px' },
                            padding: 0,
                        },
                        '& .MuiFormHelperText-root': {
                            margin: '4px 0 0 0',
                            fontSize: '12px',
                        },
                    }}
                      onChange={handleCodeChange}                                         
                      InputProps={{
                          endAdornment: code && (
                              <InputAdornment position="end">
                                  <IconButton onClick={handleClearCode} edge="end" size="small">
                                      <Box
                                          component="img"
                                          src={closeIcon}
                                          sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                      />
                                  </IconButton>
                              </InputAdornment>
                          ),
                      }}
                  />
                </Box>
                <Button
                    variant="contained"
                    sx={{
                        display: 'flex',
                        padding: { xs: '10px 20px', sm: '10px 20px' },
                        minWidth: { xs: 'auto', sm: '120px' },
                        height: { xs: '38px', sm: '44px' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        borderRadius: '16px',
                        background: countdown > 0 || emailError || !email ? 'rgba(0, 0, 0, 0.30)' : '#FF43AA',
                        color: '#FFF',
                                fontSize: { xs: '15px', sm: '16px' },
                        fontWeight: 500,
                        lineHeight: 'normal',
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: countdown > 0 || emailError || !email ? 'rgba(0, 0, 0, 0.30)' : '#FF2D9F',
                        },
                    }}
                    onClick={handleSendCode}
                    disabled={
                        countdown > 0 || 
                        emailError || 
                        !email || 
                        (type === 'findPassword' && loginType === 'email' 
                            ? !/\S+@\S+\.\S+/.test(email)  // 이메일만 검사
                            : (!/\S+@\S+\.\S+/.test(email) && !/^[0-9]+$/.test(email))  // 이메일 또는 전화번호 검사
                        )
                    }
                >
                    {countdown > 0 ? `${t('reset_password_resend')}(${countdown}s)` : (hasSentCode ? t('reset_password_resend_code') : t('reset_password_send_code'))}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
            sx={{
                display: 'flex',
                width: 'calc(100% - 40px)',                  
                maxWidth: { xs: '100%', sm: '400px' },
                justifyContent: 'flex-end',
                mt: 4,   
                position: {xs:'fixed', sm:'static'},
                left: {xs: 20},
                bottom: 20,                
                padding: '10px',
                borderRadius: '8px',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
                alignSelf: {xs:'stretch', sm:'center'},                   
                zIndex: 10,
            }}
          >
          <Button
              variant="contained"
              sx={{
                  display: 'flex',
                  height: { xs: '38px', sm: '44px' },
                  padding: { xs: '10px 30px', sm: '10px 30px' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  borderRadius: '16px',
                  background: '#FF43AA',
                  color: '#FFF',
                    fontSize: { xs: '15px', sm: '16px' },
                  fontWeight: 500,
                  lineHeight: 'normal',
                  boxShadow: 'none',
                  '&:hover': {
                      backgroundColor: '#FF2D9F',
                  },
              }}
              onClick={handleNext}
              disabled={!code || loading}
          >
              {loading ? t('processing') : t('next')}
          </Button>
        </Box>
        </>
        ) : (
        <Box sx={{ display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px',
          alignSelf: 'stretch',
          width: { xs: '100%', sm: '100%' },
          maxWidth: { xs: '100%', sm: '400px' },
          margin: { xs: 0, sm: '0 auto' },         }}>               
          {/* Content */}
          <Box sx={{ display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '40px',
            alignSelf: 'stretch' }}>
              <Box sx={{display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '40px',
                alignSelf: 'stretch' }}>
                  <Box sx={{display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch' }}>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.60)',
                      textAlign: 'center',
                            fontSize: '15px',
                      fontWeight: '500',
                      lineHeight: 'normal' }}>{t('form_id')}</Typography>
                    <Box sx={{display: 'flex',
                      height: '38px',
                      padding: '0px 16px',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      alignSelf: 'stretch' }}>
                        <Typography sx={{ color: '#211F20',
                          textAlign: 'center',
                                    fontSize: '15px',
                          fontWeight: '500',
                          lineHeight: 'normal' }}>{email || meInfo.loginId}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '20px',
                      alignSelf: 'stretch' }}>
                        <Box sx={{ display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '10px',
                          alignSelf: 'stretch' }}>
                            {type !== 'findPassword' && (
                                <TextField
                                    fullWidth
                                    type="password"
                                    placeholder={t('reset_password_current')}
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: true,
                                        endAdornment: currentPassword && (
                                            <InputAdornment position="end" sx={{ height: '38px', display: 'flex', alignItems: 'center' }}>
                                                <IconButton onClick={handleClearCurrentPassword} edge="end" size="small">
                                                    <Box
                                                        component="img"
                                                        src={closeIcon}
                                                        sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        style: {                                        
                                            padding: 0,
                                            height: 38,
                                            display: 'flex',                                        
                                        },
                                    }}
                                    sx={{
                                        display: 'flex',
                                        height: '38px',
                                        padding: '0px 16px',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        alignSelf: 'stretch',
                                        borderRadius: '16px',
                                        border: '1px solid rgba(0, 0, 0, 0.10)',
                                        background: 'rgba(0, 0, 0, 0.03)',
                                        color: 'rgba(0, 0, 0, 0.30)',
                                                                fontSize: '15px',
                                        fontWeight: '500',
                                        lineHeight: 'normal',
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            height: 38,
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                />
                            )}
                            <TextField
                                fullWidth
                                type="password"
                                placeholder={t('form_password')}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    endAdornment: password && (
                                        <InputAdornment position="end" sx={{ height: '38px', display: 'flex', alignItems: 'center' }}>
                                            <IconButton onClick={handleClearPassword} edge="end" size="small">
                                                <Box
                                                    component="img"
                                                    src={closeIcon}
                                                    sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    style: {                                     
                                        padding: 0,
                                        height: 38,
                                        display: 'flex',                                      
                                    },
                                }}
                                sx={{
                                    display: 'flex',
                                    height: '38px',
                                    padding: '0px 16px',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    alignSelf: 'stretch',
                                    borderRadius: '16px',
                                    border: '1px solid rgba(0, 0, 0, 0.10)',
                                    background: 'rgba(0, 0, 0, 0.03)',
                                    color: 'rgba(0, 0, 0, 0.30)',
                                                        fontSize: '15px',
                                    fontWeight: '500',
                                    lineHeight: 'normal',
                                    '& .MuiInputBase-input': {
                                        padding: 0,
                                        height: 38,
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                type="password"
                                placeholder={t('reset_password_confirm')}
                                value={passwordConfirm}
                                onChange={e => setPasswordConfirm(e.target.value)}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    endAdornment: passwordConfirm && (
                                        <InputAdornment position="end" sx={{ height: '38px', display: 'flex', alignItems: 'center' }}>
                                            <IconButton onClick={handleClearPasswordConfirm} edge="end" size="small">
                                                <Box
                                                    component="img"
                                                    src={closeIcon}
                                                    sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    style: {                                     
                                        padding: 0,
                                        height: 38,
                                        display: 'flex',                                      
                                    },
                                }}
                                sx={{
                                    display: 'flex',
                                    height: '38px',
                                    padding: '0px 16px',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    alignSelf: 'stretch',
                                    borderRadius: '16px',
                                    border: '1px solid rgba(0, 0, 0, 0.10)',
                                    background: 'rgba(0, 0, 0, 0.03)',
                                    color: 'rgba(0, 0, 0, 0.30)',
                                                        fontSize: '15px',
                                    fontWeight: '500',
                                    lineHeight: 'normal',
                                    '& .MuiInputBase-input': {
                                        padding: 0,
                                        height: 38,
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            />
                            <Typography sx={{ color: '#FF0000',
                                            fontSize: '12px',
                              fontWeight: '500',
                              lineHeight: 'normal',
                              letterSpacing: '-0.36px' }}>
                                {!isValidPassword(password) ? t('auth_invalid_password') :
                                 password !== passwordConfirm ? t('auth_password_mismatch') : ''}
                            </Typography>
                          </Box>
                        </Box>                    
                      </Box>
                </Box>
                {/* Bottom button */}
                <Box
                  sx={{ 
                      display: 'flex',
                      width: 'calc(100% - 40px)',                  
                      maxWidth: { xs: '100%', sm: '400px' },
                      justifyContent: 'flex-end',
                      mt: 4,   
                      position: {xs:'fixed', sm:'static'},
                      left: {xs: 20},
                      bottom: 20,                
                      padding: '10px',
                      borderRadius: '8px',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '20px',
                      alignSelf: {xs:'stretch', sm:'center'},                   
                      zIndex: 10,
                  }}
                >
                  <Button
                      variant="contained"
                      sx={{
                          display: 'flex',
                          height: { xs: '38px', sm: '44px' },
                          padding: { xs: '10px 30px', sm: '10px 30px' },
                          justifyContent: 'center',
                          alignItems: 'center',
                          alignSelf: 'stretch',
                          borderRadius: '16px',
                          background: 'rgba(0, 0, 0, 0.30)',
                          color: '#FFF',
                                    fontSize: { xs: '15px', sm: '16px' },
                          fontWeight: 500,
                          lineHeight: 'normal',
                          boxShadow: 'none',
                          '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.50)',
                          },
                      }}
                      onClick={() => navigate(-1)}
                  >
                      {t('common_cancel')}
                  </Button>
                  <Button
                      variant="contained"
                      sx={{
                          display: 'flex',
                          height: { xs: '38px', sm: '44px' },
                          padding: { xs: '10px 30px', sm: '10px 30px' },
                          justifyContent: 'center',
                          alignItems: 'center',
                          alignSelf: 'stretch',
                          borderRadius: '16px',
                          background: '#FF43AA',
                          color: '#FFF',
                                    fontSize: { xs: '15px', sm: '16px' },
                          fontWeight: 500,
                          lineHeight: 'normal',
                          boxShadow: 'none',
                          '&:hover': {
                              backgroundColor: '#FF2D9F',
                          },
                      }}
                      onClick={handleChangePassword}
                      disabled={
                        loading ||
                        !(currentPassword && password && passwordConfirm && 
                          password === passwordConfirm && 
                          isValidPassword(password))
                      }
                  >
                      {loading ? t('processing') : t('common_confirm')}
                  </Button>
                </Box>           
              </Box>
          )}
        </Box>
    );
};
